import {ResponsiveService} from "../responsive/responsive.service";
import {Color} from "../ui-elements/color";
import {CssVarHelper} from '../utils/css-var.helper';
import {titleCase} from '../utils/string-utils';
import {TranslateService} from "@ngx-translate/core";
import {effect, inject, Injectable, signal, WritableSignal} from "@angular/core";
import {
    AppCardThemeData,
    AppCustomTextData,
    AppData,
    AppThemePaletteEntryData,
    AppThemeSettingValueData,
    ColorPairData
} from '../configuration/app-entity.model';
import {ConfigurationModel} from '../configuration/configuration.model';

@Injectable({providedIn: "root"})
export class ThemeModel {
    private configurationModel: ConfigurationModel = inject(ConfigurationModel);
    private responsiveService: ResponsiveService = inject(ResponsiveService);
    private translate: TranslateService = inject(TranslateService);

    private static chewziePalette: {
        name: string,
        colorPair: { backgroundColor: string, foregroundColor: string }
    }[] = [
        {
            name: "brand",
            colorPair: {
                backgroundColor: "#ff6450",
                foregroundColor: "#ffffff"
            }
        },
        {
            name: "link",
            colorPair: {
                backgroundColor: "n/a",
                foregroundColor: "#ffdb3d"
            }
        },
        {
            name: "standard-page",
            colorPair: {
                backgroundColor: "#121212",
                foregroundColor: "#ffffff"
            }
        },
        {
            name: "standard-page-header",
            colorPair: {
                backgroundColor: "#1e1e1e",
                foregroundColor: "#ffffff"
            }
        },
        {
            name: "panel",
            colorPair: {
                backgroundColor: "#121212",
                foregroundColor: "#ffffff"
            }
        },
        {
            name: "error-message",
            colorPair: {
                backgroundColor: "#ffc3b9",
                foregroundColor: "#000000"
            }
        },
        {
            name: "warning-message",
            colorPair: {
                backgroundColor: "#ffd484",
                foregroundColor: "#000000"
            }
        },
        {
            name: "success-message",
            colorPair: {
                backgroundColor: "#c5ead6",
                foregroundColor: "#000000"
            }
        },
        {
            name: "info-message",
            colorPair: {
                backgroundColor: "#e0e0e0",
                foregroundColor: "#000000"
            }
        },
        {
            name: "danger",
            colorPair: {
                backgroundColor: "#ee0000",
                foregroundColor: "#ffffff"
            }
        },
        {
            name: "success",
            colorPair: {
                backgroundColor: "n/a",
                foregroundColor: "#31d066"
            }
        }
    ];

    public readonly isLightTheme: WritableSignal<boolean> = signal<boolean>(true);

    private appliedAppCssVars: string[] = [];
    private appliedPaletteMap: { [paletteEntryName: string]: ColorPairData } = {};

    constructor() {
        console.log("--- ThemeModel");

        effect(() => {
            const appData: AppData | undefined = this.configurationModel.app();
            if (appData) {
                this.applyAppSettings(appData);
            }
        });

        if (this.configurationModel.isWaiterActor()) {
            this.applyAppSettings();
        }
    }

    private static readonly overridePaletteDefaults: { from: string, to: string } [] = [
        {from: "standard-page", to: "menu-page-image"},
        {from: "standard-page", to: "standard-page-image"},
        {from: "standard-page", to: "alert"},
        {from: "standard-page-header", to: "panel-header-footer"},
        {from: "panel-header-footer", to: "header-image"}
    ];

    public getPaletteEntry(name: string): ColorPairData {
        return this.appliedPaletteMap[name];
    }

    private applyAppSettings(app?: AppData): void {
        const cssVars: Record<string, string | number> = {};

        document.title = app ? app.appName : undefined;

        this.appliedPaletteMap = {};
        this.appliedAppCssVars.forEach((cssVar: string) => cssVars[cssVar] = undefined);

        // Apply specific app settings
        if (app && !app.desktopPillarboxing) {
            cssVars["--woa-desktop-pillarbox-width"] = "10000px";
        }

        // Apply palette color-pairs and foreground-colors
        const palette: {
            name: string,
            colorPair: { backgroundColor: string, foregroundColor: string }
        }[] = this.configurationModel.isWaiterActor() ? ThemeModel.chewziePalette : app.palette;
        palette.forEach((paletteEntry: AppThemePaletteEntryData) => {
            this.applyPaletteEntry(paletteEntry.name, paletteEntry.colorPair, cssVars);

            // Set derived dark/light colors
            if (paletteEntry.name === "panel") {
                [cssVars["--woa-theme-color-auto-dark-panel"], cssVars["--woa-theme-color-auto-light-panel"]] = Color.darkLightRGB(paletteEntry);
            }
            if (paletteEntry.name === "standard-page") {
                this.isLightTheme.set(Color.fromString(paletteEntry.colorPair.backgroundColor).lightness601 >= 0.5);
                cssVars["--woa-page-shroud-alpha"] = this.isLightTheme() ? "var(--woa-page-light-theme-shroud-alpha)" : "var(--woa-page-dark-theme-shroud-alpha)";
            }
        });

        // Handle defaults for known cases of context-overrides where the context color-pair is defaulted in scss,
        // and the override has not been set explicitly.
        ThemeModel.overridePaletteDefaults.forEach(({from, to}) => this.applyDefaultOverrides(from, to, cssVars));

        if (app) {

            // Apply card themes
            if (app.cardThemes) {
                app.cardThemes.forEach((cardTheme: AppCardThemeData) => {
                    const cardThemeName: string = cardTheme.name.replace(" ", "_");
                    if (cardTheme.widthPx != undefined) {
                        cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-width"] = cardTheme.widthPx + "px";
                        cssVars["--woa-" + cardThemeName + "-themed-ticket-width"] = cardTheme.widthPx + "px";
                    }
                    if (cardTheme.heightPx != undefined) {
                        cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-height"] = cardTheme.heightPx + "px";
                        cssVars["--woa-" + cardThemeName + "-themed-ticket-height"] = cardTheme.heightPx + "px";
                    }
                    if (cardTheme.bodyPadding != undefined) {
                        cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-body-padding"] = cardTheme.bodyPadding;
                        cssVars["--woa-" + cardThemeName + "-themed-ticket-body-padding"] = cardTheme.bodyPadding;
                    }
                    if (cardTheme.backgroundColor != undefined) {
                        cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-background-color"] = cardTheme.backgroundColor;
                        cssVars["--woa-" + cardThemeName + "-themed-ticket-background-color"] = cardTheme.backgroundColor;
                    }
                    if (cardTheme.maxColumnSpec != undefined) {
                        cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-max-column-spec"] = cardTheme.maxColumnSpec;
                        cssVars["--woa-" + cardThemeName + "-themed-ticket-max-column-spec"] = cardTheme.maxColumnSpec;
                    }
                    if (cardTheme.headerBackgroundColor != undefined && cardTheme.headerBackgroundOpacity != undefined && cardTheme.headerTextColor != undefined) {
                        cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-header-background-color"] = cardTheme.headerBackgroundColor;
                        cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-header-background-opacity"] = cardTheme.headerBackgroundOpacity;
                        cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-header-text-color"] = cardTheme.headerTextColor;

                        cssVars["--woa-" + cardThemeName + "-themed-ticket-header-background-color"] = cardTheme.headerBackgroundColor;
                        cssVars["--woa-" + cardThemeName + "-themed-ticket-header-background-opacity"] = cardTheme.headerBackgroundOpacity;
                        cssVars["--woa-" + cardThemeName + "-themed-ticket-header-text-color"] = cardTheme.headerTextColor;
                    }
                    if (cardTheme.footerBackgroundColor != undefined && cardTheme.footerBackgroundOpacity != undefined && cardTheme.footerTextColor != undefined) {
                        cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-footer-background-color"] = cardTheme.footerBackgroundColor;
                        cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-footer-background-opacity"] = cardTheme.footerBackgroundOpacity;
                        cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-footer-text-color"] = cardTheme.footerTextColor;

                        cssVars["--woa-" + cardThemeName + "-themed-ticket-footer-background-color"] = cardTheme.footerBackgroundColor;
                        cssVars["--woa-" + cardThemeName + "-themed-ticket-footer-background-opacity"] = cardTheme.footerBackgroundOpacity;
                        cssVars["--woa-" + cardThemeName + "-themed-ticket-footer-text-color"] = cardTheme.footerTextColor;
                    }
                    if (cardTheme.placeholderBackgroundColor != undefined && cardTheme.placeholderOpacity != undefined && cardTheme.placeholderBorderColor != undefined) {
                        cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-placeholder-background-color"] = cardTheme.placeholderBackgroundColor;
                        cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-placeholder-opacity"] = cardTheme.placeholderOpacity;
                        cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-placeholder-border-color"] = cardTheme.placeholderBorderColor;

                        cssVars["--woa-" + cardThemeName + "-themed-ticket-placeholder-background-color"] = cardTheme.placeholderBackgroundColor;
                        cssVars["--woa-" + cardThemeName + "-themed-ticket-placeholder-opacity"] = cardTheme.placeholderOpacity;
                        cssVars["--woa-" + cardThemeName + "-themed-ticket-placeholder-border-color"] = cardTheme.placeholderBorderColor;
                    }
                    if (cardTheme.stampColor != undefined && cardTheme.stampOpacity != undefined) {
                        cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-stamp-color"] = cardTheme.stampColor;
                        cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-stamp-opacity"] = cardTheme.stampOpacity;

                        cssVars["--woa-" + cardThemeName + "-themed-ticket-stamp-color"] = cardTheme.stampColor;
                        cssVars["--woa-" + cardThemeName + "-themed-ticket-stamp-opacity"] = cardTheme.stampOpacity;
                    }
                    if (cardTheme.svgStampIcon != undefined) {
                        cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-stamp-icon"] = cardTheme.svgStampIcon;
                        cssVars["--woa-" + cardThemeName + "-themed-ticket-stamp-icon"] = cardTheme.svgStampIcon;
                    }
                    if (cardTheme.stampIconScale != undefined) {
                        cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-stamp-icon-scale"] = cardTheme.stampIconScale;
                        cssVars["--woa-" + cardThemeName + "-themed-ticket-stamp-icon-scale"] = cardTheme.stampIconScale;
                    }
                    if (cardTheme.stampIconRotateFactor != undefined) {
                        cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-stamp-icon-rotate-factor"] = cardTheme.stampIconRotateFactor;
                        cssVars["--woa-" + cardThemeName + "-themed-ticket-stamp-icon-rotate-factor"] = cardTheme.stampIconRotateFactor;
                    }
                });
            }

            // Apply custom style settings
            this.configurationModel.app()?.themeSettings.forEach((setting: AppThemeSettingValueData) => {
                cssVars[setting.key] = setting.value;
            });
        }

        this.appliedAppCssVars = Object.keys(cssVars);
        CssVarHelper.applyVars(document.documentElement, cssVars);

        if (app) {

            // Apply app settings and custom text to the translation service
            this.translate.set("woa.common.appName", this.configurationModel.app()?.appName);
            if (this.configurationModel.app()?.shortAppName) {
                this.translate.set("woa.common.shortAppName", this.configurationModel.app()?.shortAppName, "en");
            }

            if (this.configurationModel.app()?.loginText1) {
                this.translate.set("woa.message.entry.welcome.line1", this.configurationModel.app()?.loginText1, "en");
            }
            if (this.configurationModel.app()?.loginText2) {
                this.translate.set("woa.message.entry.welcome.line2", this.configurationModel.app()?.loginText2, "en");
            }

            if (this.configurationModel.app()?.memberNumberName) {
                this.translate.set("woa.common.memberNumberName", this.configurationModel.app()?.memberNumberName, "en");
            }

            if (this.configurationModel.app()?.pointsName) {
                this.translate.set("woa.common.pointsName", this.configurationModel.app()?.pointsName.toLowerCase(), "en");
                this.translate.set("woa.common.PointsName", titleCase(this.configurationModel.app()?.pointsName), "en");
            }

            if (this.configurationModel.app()?.tableOrderingName) {
                this.translate.set("woa.common.order.atTable.placement.noun.lc", this.configurationModel.app()?.tableOrderingName.toLowerCase(), "en");
                this.translate.set("woa.common.order.atTable.placement.noun.uc", titleCase(this.configurationModel.app()?.tableOrderingName), "en");

                if (this.configurationModel.app()?.tableOrderingName.toLowerCase() === "room") {
                    this.translate.set("woa.common.order.atTable.placement.orderPhrase.preposition", "in", "en");
                    this.translate.set("woa.common.order.atTable.number.noun.lc", "room number", "en");
                    this.translate.set("woa.common.order.atTable.number.noun.uc", "Room Number", "en");
                }
            }

            this.configurationModel.app()?.customTexts.forEach((appCustomText: AppCustomTextData) => {
                this.translate.set(appCustomText.key, appCustomText.value);
            });

            this.responsiveService.setModeToWindowWidth();
        }
    }

    private applyPaletteEntry(paletteEntryName: string, colorPair: ColorPairData, cssVars: Record<string, string | number>): void {
        if (colorPair.backgroundColor !== "n/a") {
            if (colorPair.backgroundColor) {
                cssVars["--woa-theme-color-" + paletteEntryName + "-pair-background"] = Color.colorStringToRGB(colorPair.backgroundColor);
                cssVars["--woa-theme-color-" + paletteEntryName + "-pair-background-shader"] = Color.colorStringToShaderRGB(colorPair.backgroundColor, true);
            }
            if (colorPair.foregroundColor) {
                cssVars["--woa-theme-color-" + paletteEntryName + "-pair-foreground"] = Color.colorStringToRGB(colorPair.foregroundColor);
                cssVars["--woa-theme-color-" + paletteEntryName + "-pair-foreground-shader"] = Color.colorStringToShaderRGB(colorPair.foregroundColor);
            }
        } else if (colorPair.foregroundColor) {
            const i: number = paletteEntryName.indexOf("/");
            if (i >= 0) {
                const pairName: string = paletteEntryName.substring(0, i);
                const variantName: string = paletteEntryName.substring(i + 1);
                cssVars["--woa-theme-color-" + pairName + "-pair-" + variantName + "-foreground"] = Color.colorStringToRGB(colorPair.foregroundColor);
            } else {
                cssVars["--woa-theme-color-" + paletteEntryName + "-foreground"] = Color.colorStringToRGB(colorPair.foregroundColor);
                cssVars["--woa-theme-color-" + paletteEntryName + "-foreground-shader"] = Color.colorStringToShaderRGB(colorPair.foregroundColor);
            }
        }

        // Handle additional defaults for overrides of palette entries which are both a color-pair and a foreground
        if (paletteEntryName.endsWith("--brand")) {
            cssVars["--woa-theme-color-" + paletteEntryName + "-foreground"] = Color.colorStringToRGB(colorPair.backgroundColor);
        }

        this.appliedPaletteMap[paletteEntryName] = colorPair;
    }

    /**
     * Where an override has been provided for fromContext, apply that same override for toContext if it has not been specified.
     * This caters for context palette entries which default from another context palette entry.
     * @param fromContext
     * @param toContext
     * @param cssVars records all applied CSS custom properties
     * @param assumeFromContextIsSet
     */
    private applyDefaultOverrides(fromContext: string, toContext: string, cssVars: Record<string, string | number>, assumeFromContextIsSet: boolean = false): void {
        if ((assumeFromContextIsSet || this.appliedPaletteMap[fromContext]) && !this.appliedPaletteMap[toContext]) {
            Object.keys(this.appliedPaletteMap).forEach((name: string) => {
                if (name.startsWith(fromContext + "--")) {
                    const overrideName: string = toContext + name.substring(fromContext.length);
                    if (!this.appliedPaletteMap[overrideName]) {
                        this.applyPaletteEntry(overrideName, this.appliedPaletteMap[name], cssVars);
                    }
                }
            });
        }
    }
}
